import { useEffect, useRef } from 'react';
import {
    useChecklistData,
    useInvalidateChecklistQuery,
} from 'components/required-checklist/useChecklistData';
import { CHECKLIST_MAX_TIME_AGO } from 'contexts/onboarding/createOnboardingRequiredChecklistSlice';
import { useAccountLocalStorage } from 'contexts/useAccountLocalStorage';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { isoDateFromServer } from 'helpers/dates';
import { DateTime } from 'luxon';

const useRequiredChecklistInit = () => {
    const { accountId } = useCurrentAccount();
    const { checklist, data, completedAllActions, isRefetching } = useChecklistData();
    const isFirstFetch = useRef(true);
    const { invalidateChecklistQuery } = useInvalidateChecklistQuery();

    useEffect(() => {
        isFirstFetch.current = true;
        invalidateChecklistQuery();
    }, [accountId, invalidateChecklistQuery]);

    const {
        hasSeenChecklistCompleteDialog,
        setCompletedRequiredChecklistThisSession,
        completedRequiredChecklistBetweenSessions,
        setCompletedRequiredChecklistBetweenSessions,
        setWasCompletedOverMaxTimeAgo,
    } = useAccountLocalStorage(
        ({
            hasSeenChecklistCompleteDialog,
            setCompletedRequiredChecklistThisSession,
            completedRequiredChecklistBetweenSessions,
            setCompletedRequiredChecklistBetweenSessions,
            setWasCompletedOverMaxTimeAgo,
        }) => ({
            hasSeenChecklistCompleteDialog,
            setCompletedRequiredChecklistThisSession,
            completedRequiredChecklistBetweenSessions,
            setCompletedRequiredChecklistBetweenSessions,
            setWasCompletedOverMaxTimeAgo,
        }),
    );

    useEffect(() => {
        if (!checklist || isRefetching) {
            return;
        }

        if (completedAllActions) {
            setWasCompletedOverMaxTimeAgo(isChecklistStale(data));
        }

        if (!hasSeenChecklistCompleteDialog && completedAllActions) {
            if (isFirstFetch.current) {
                setCompletedRequiredChecklistBetweenSessions(true);
            } else if (!completedRequiredChecklistBetweenSessions) {
                setCompletedRequiredChecklistThisSession(true);
            }
        }

        isFirstFetch.current = false;
    }, [
        checklist,
        data,
        completedAllActions,
        hasSeenChecklistCompleteDialog,
        completedRequiredChecklistBetweenSessions,
        isRefetching,
        setCompletedRequiredChecklistBetweenSessions,
        setCompletedRequiredChecklistThisSession,
        setWasCompletedOverMaxTimeAgo,
    ]);
};

const isChecklistStale = (data: ReturnType<typeof useChecklistData>['data']) => {
    const completedTimestamps = data
        .map(action => action.completed_at)
        .filter(completedAt => completedAt !== null);

    if (data.length !== completedTimestamps.length) {
        return false;
    }

    const mostRecentCompletion = completedTimestamps.reduce((prev, curr) => {
        return new Date(curr) > new Date(prev) ? curr : prev;
    }, completedTimestamps[0]);

    return isoDateFromServer(mostRecentCompletion) < DateTime.now().minus(CHECKLIST_MAX_TIME_AGO);
};

export { useRequiredChecklistInit };
